import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { ResponsiveBar, type BarDatum } from '@nivo/bar';
import { ResponsiveLine } from '@nivo/line';
import { ResponsivePie, type ComputedDatum, type LegendDatum } from '@nivo/pie';
import { ResponsiveScatterPlot } from '@nivo/scatterplot';
import { ArrowDownTrayIcon, TableCellsIcon } from '@heroicons/react/24/outline';
import SharedBadge from '@/global/Badge/Badge';
import SharedButton from '@/global/Button/Button';
import { getSourceDisplayName } from '@/modules/search/searchGrid/helpers/sourceTabMetadata';
import Loader from '../../Common/Loader';
import { NoRecordsFound } from '../NoRecordsFound';
import { buildAnalyticsFileStem, downloadAnalyticsPng } from './analyticsDownload';
import {
  BRAND_NAVY,
  BRAND_PINK,
  CT_COLOR_BUCKETS,
  averageField,
  buildAnalyticsSearchTitle,
  formatMetricNumber,
  getCumulativePublicationsByYear,
  getMetricDistribution,
  getMetricScatterData,
  getSummaryCardLabel,
  getSummaryCardValue,
  getTopEntitiesByMetric,
  pickRandomScatterLabelIds,
} from './analyticsHelpers';
import { getAvailableChartableColumns, getChartableByField } from './chartableColumns';
import { ColumnChartWidget } from './ColumnChartWidget';
import { navigateToScoreBucketTable, navigateToSelectedEntityTable } from './analyticsNavigation';
import { renderTruncatedBarChartTick } from './analyticsBarChartTicks';
import { ScatterPlotLabelsLayer } from './analyticsScatterLabels';
import type { AnalyticsTabKey } from './analyticsConfig';
import type { ScoreDistributionSlice } from './analyticsTypes';
import { ChartPanel, MetricSelect, SummaryCard } from './analyticsUi';
import { useAssociationAnalyticsData } from './useAssociationAnalyticsData';
import { useSearchEvidenceSample } from './useSearchEvidenceSample';
import { isEvidenceProfileEnabled } from '@/config/evidenceProfile';
import { EvidenceDistributionGrid } from '../EvidenceAnalytics/EvidenceDistributionGrid';
import { useSummaryMatchContext } from '@/modules/search/searchGrid/context/SummaryMatchContext';
import { describeEvidenceCoverage } from '@/utils/fetchEvidenceDistribution';
import AcademicPlanFeatureGate from '@/components/Modal/AcademicPlanFeatureGate';
import { useAuth } from '@/store/hooks/useAuth';
import { isAcademicPlanFeatureRestricted } from '@/utils/premiumPlanFeatures';
import { trackEvent } from '@/utils/analytics';
import { DashboardCustomizer } from './DashboardCustomizer';
import {
  filterMetricsByCapability,
  formatMetricValue,
  getAvailableMetrics,
  getDomainScale,
  getMetricByField,
} from './metricCatalog';
import { useDashboardLayout } from './useDashboardLayout';
import {
  getPanelDefinition,
  isPresetSummaryCard,
  resolveScatterTitle,
  resolveTopEntitiesTitle,
  type ColumnChartParams,
  type MetricScatterParams,
  type PanelWidgetInstance,
  type ScoreDistributionParams,
  type TopEntitiesParams,
} from './widgetRegistry';
import { resolveWidgetSpans } from './widgetSpans';
import './GdaAnalytics.css';

const CHART_THEME = {
  axis: {
    legend: {
      text: {
        fontSize: 12,
        fontWeight: 600,
        fill: '#0a104f',
      },
    },
    ticks: {
      text: {
        fontSize: 11,
        fill: '#323f57',
      },
    },
  },
  grid: {
    line: {
      stroke: '#e8eaef',
      strokeWidth: 1,
    },
  },
  legends: {
    text: {
      fontSize: 11,
      fill: '#323f57',
    },
  },
};

interface AssociationAnalyticsDashboardProps {
  associationTab: AnalyticsTabKey;
  source: string;
  view: string;
  names: Array<{ id: string; name?: string; label?: string }>;
}

function AssociationAnalyticsDashboard({
  associationTab,
  source,
  view,
  names,
}: AssociationAnalyticsDashboardProps) {
  const navigate = useNavigate();
  const { isAcademic, isAuthenticated } = useAuth();
  const isEvidenceProfileRestricted = isAcademicPlanFeatureRestricted(isAcademic, isAuthenticated);
  const dashboardRef = useRef<HTMLDivElement>(null);
  const graphsContentRef = useRef<HTMLDivElement>(null);
  const [entityPickerMaxHeight, setEntityPickerMaxHeight] = useState<number | undefined>(undefined);
  const [selectedEntityId, setSelectedEntityId] = useState<string>(() => names?.[0]?.id ?? '');
  const [isDownloadingPng, setIsDownloadingPng] = useState(false);

  useEffect(() => {
    if (!names?.length) return;
    const stillExists = names.some(item => item.id === selectedEntityId);
    if (!stillExists) setSelectedEntityId(names[0].id);
  }, [names, selectedEntityId]);

  const selectedEntity = useMemo(
    () => names.find(item => item.id === selectedEntityId) ?? names[0],
    [names, selectedEntityId],
  );
  const selectedNames = selectedEntity ? [selectedEntity] : [];

  const { data, isLoading, isEmpty, config, isPanelActive } = useAssociationAnalyticsData(
    source,
    view,
    associationTab,
    selectedNames,
  );

  const {
    catalog,
    chartableColumns,
    layout,
    visiblePanels,
    visibleCards,
    isCustomized,
    columnChartCount,
    maxColumnCharts,
    togglePanel,
    toggleCard,
    movePanel,
    reorderPanel,
    updatePanelParams,
    updateCardMetric,
    addColumnChart,
    addColumnCharts,
    removePanel,
    resetLayout,
  } = useDashboardLayout(config, view, data);

  const availableCatalog = useMemo(() => getAvailableMetrics(catalog, data), [catalog, data]);
  const availableChartableColumns = useMemo(
    () => getAvailableChartableColumns(chartableColumns, data),
    [chartableColumns, data],
  );
  const rankableMetrics = useMemo(
    () => filterMetricsByCapability(availableCatalog, 'rankable'),
    [availableCatalog],
  );
  const axisMetrics = useMemo(() => filterMetricsByCapability(availableCatalog, 'axis'), [availableCatalog]);

  const summaryMatchContext = useSummaryMatchContext();
  const summaryMatchCardContext = useMemo(() => {
    if (
      !summaryMatchContext.isActive ||
      String(summaryMatchContext.tab ?? '').toUpperCase() !== String(associationTab).toUpperCase()
    ) {
      return null;
    }
    return {
      stats: summaryMatchContext.stats,
      resultTotalCount: summaryMatchContext.resultTotalCount,
    };
  }, [
    associationTab,
    summaryMatchContext.isActive,
    summaryMatchContext.resultTotalCount,
    summaryMatchContext.stats,
    summaryMatchContext.tab,
  ]);

  const { metrics: evidenceMetrics, isLoading: isEvidenceLoading } = useSearchEvidenceSample({
    source,
    view,
    associationTab,
    searchItems: selectedNames,
    enabled: isEvidenceProfileEnabled && isPanelActive && !isEmpty,
  });
  const evidenceCoverage = describeEvidenceCoverage(evidenceMetrics);

  const searchTitle = buildAnalyticsSearchTitle(selectedNames);
  const sourceLabel = getSourceDisplayName(source);
  const currentYear = new Date().getFullYear();

  const showEntityPicker = names.length > 1;
  const entityPickerTitle =
    view === 'DISEASES'
      ? 'Diseases'
      : view === 'GENES'
        ? 'Genes'
        : view === 'VARIANTS'
          ? 'Variants'
          : 'Entities';

  useLayoutEffect(() => {
    const el = graphsContentRef.current;
    if (!el || !showEntityPicker) {
      setEntityPickerMaxHeight(undefined);
      return;
    }

    const media = window.matchMedia('(min-width: 1201px)');

    const update = () => {
      if (!media.matches) {
        setEntityPickerMaxHeight(undefined);
        return;
      }
      const height = el.clientHeight || el.getBoundingClientRect().height;
      if (Number.isFinite(height) && height > 0) {
        setEntityPickerMaxHeight(Math.round(height));
      }
    };

    update();
    requestAnimationFrame(() => update());

    const onMediaChange = () => update();
    media.addEventListener('change', onMediaChange);

    if (typeof ResizeObserver === 'undefined') {
      return () => media.removeEventListener('change', onMediaChange);
    }

    const ro = new ResizeObserver(() => update());
    ro.observe(el);
    return () => {
      ro.disconnect();
      media.removeEventListener('change', onMediaChange);
    };
  }, [showEntityPicker, isLoading, isEmpty, selectedEntityId, config, visiblePanels, layout]);

  const fileStem = useMemo(
    () => (config ? buildAnalyticsFileStem(config, searchTitle) : 'analytics'),
    [config, searchTitle],
  );

  const handleDownloadPng = async () => {
    if (!config || isDownloadingPng) return;
    setIsDownloadingPng(true);
    try {
      await downloadAnalyticsPng(dashboardRef.current, `${fileStem}.png`);
    } finally {
      setIsDownloadingPng(false);
    }
  };

  const trackMetricChange = (widgetType: string, param: string, metric: string) => {
    trackEvent({
      name: 'dashboard_metric_change',
      buttonId: `dashboard_metric_change_${widgetType}_${param}`,
      page: associationTab,
      section: 'analytics',
      feature: 'dashboard_customization',
      eventCategory: 'dashboard_customization',
      additionalParams: { widget_type: widgetType, param, metric },
    });
  };

  if (!config || !layout) {
    return <NoRecordsFound />;
  }

  if (!isPanelActive || isLoading) {
    return (
      <div className="gda-analytics-loading">
        <Loader />
      </div>
    );
  }

  if (isEmpty) {
    return <NoRecordsFound />;
  }

  const renderTopEntities = (panel: PanelWidgetInstance) => {
    const params = panel.params as TopEntitiesParams;
    const metricField = params.metric || config.fields.score;
    const metric = getMetricByField(availableCatalog, metricField) ?? getMetricByField(catalog, metricField);
    const topEntities = getTopEntitiesByMetric(data, config, metricField, 15);
    const topBarData = topEntities.map(entity => ({
      entityId: entity.entityId,
      normalizedScore: entity.normalizedScore,
      numPMIDs: entity.numPMIDs,
      numCTs: entity.numCTs,
      color: entity.color,
      barValue: entity.metricValue,
    })) as BarDatum[];

    const title = resolveTopEntitiesTitle(config, metric);
    const metricOptions = rankableMetrics.map(entry => ({
      label: entry.label,
      value: entry.field,
    }));

    return (
      <ChartPanel
        title={title}
        className="gda-analytics-panel--wide"
        headerExtra={
          metricOptions.length > 1 ? (
            <MetricSelect
              label="Rank by"
              value={metricField}
              options={metricOptions}
              onChange={value => {
                updatePanelParams(panel.id, { metric: value });
                trackMetricChange('topEntities', 'metric', value);
              }}
            />
          ) : null
        }
      >
        <div className="gda-analytics-bar-chart">
          <ResponsiveBar
            data={topBarData}
            keys={['barValue']}
            indexBy="entityId"
            layout="vertical"
            margin={{ top: 16, right: 24, bottom: 84, left: 56 }}
            padding={0.28}
            colors={bar => String(bar.data.color ?? '#0a104f')}
            theme={CHART_THEME}
            axisBottom={{
              tickRotation: -45,
              tickSize: 0,
              tickPadding: 12,
              legend: config.labels.entityAxis,
              legendPosition: 'middle',
              legendOffset: 64,
              renderTick: renderTruncatedBarChartTick,
            }}
            axisLeft={{
              legend: metric?.label ?? config.labels.scoreAxis,
              legendPosition: 'middle',
              legendOffset: -48,
            }}
            enableLabel={false}
            borderRadius={5}
            tooltip={({ indexValue, value, data: barData }) => (
              <div className="gda-analytics-tooltip">
                <strong>{indexValue}</strong>
                <div>
                  {metric?.label ?? config.labels.scoreAxis}:{' '}
                  {formatMetricValue(Number(value), metric?.format ?? 'decimal3')}
                </div>
                {config.features.ctLegend ? <div>Clinical trials: {Number(barData.numCTs ?? 0)}</div> : null}
                {metricField !== config.fields.score ? (
                  <div>
                    {config.labels.scoreAxis}: {Number(barData.normalizedScore ?? 0).toFixed(3)}
                  </div>
                ) : null}
              </div>
            )}
          />
        </div>
        {config.features.ctLegend ? (
          <div className="gda-analytics-legend">
            {CT_COLOR_BUCKETS.map(bucket => (
              <span key={bucket.label} className="gda-analytics-legend__item">
                <span className="gda-analytics-legend__swatch" style={{ backgroundColor: bucket.color }} />
                {bucket.label}
              </span>
            ))}
          </div>
        ) : null}
      </ChartPanel>
    );
  };

  const renderPublicationTrend = () => {
    if (!config.fields.yearFinal) return null;
    const publicationTrend = getCumulativePublicationsByYear(
      data,
      config.fields.yearFinal,
      config.fields.pmids,
    );
    const publicationLineData = [
      {
        id: 'Cumulative publications',
        data: publicationTrend.map(point => ({
          x: String(point.year),
          y: point.cumulative,
        })),
      },
    ];

    return (
      <ChartPanel title="Cumulative publications by year">
        <div className="gda-analytics-line-chart">
          <ResponsiveLine
            data={publicationLineData}
            margin={{ top: 16, right: 24, bottom: 52, left: 64 }}
            xScale={{ type: 'point' }}
            yScale={{ type: 'linear', min: 'auto', max: 'auto' }}
            curve="monotoneX"
            axisBottom={{
              legend: 'Year',
              legendPosition: 'middle',
              legendOffset: 40,
              tickRotation: -50,
            }}
            axisLeft={{
              legend: 'Cumulative PMIDs',
              legendPosition: 'middle',
              legendOffset: -52,
            }}
            colors={['#0a104f']}
            pointSize={6}
            pointColor="#ffffff"
            pointBorderWidth={2}
            pointBorderColor={{ from: 'serieColor' }}
            enableGridX={false}
            theme={CHART_THEME}
            useMesh
            tooltip={({ point }) => (
              <div className="gda-analytics-tooltip">
                <strong>{point.data.x}</strong>
                <div>Cumulative: {Number(point.data.y).toLocaleString('es-ES')}</div>
              </div>
            )}
          />
        </div>
      </ChartPanel>
    );
  };

  const renderScoreDistribution = (panel: PanelWidgetInstance) => {
    const params = panel.params as ScoreDistributionParams;
    const metricField = params.metric || config.fields.score;
    const metric = getMetricByField(availableCatalog, metricField) ?? getMetricByField(catalog, metricField);
    const distribution = getMetricDistribution(data, metricField, metric?.domain ?? { kind: 'unit' });
    const distributionById = new Map(distribution.map(slice => [slice.id, slice]));
    const canFilter = Boolean(metric?.filterable && metric.field === config.filterScoreField);

    const handleScoreBucketClick = (slice: ScoreDistributionSlice) => {
      if (!canFilter || !slice.value) return;
      navigateToScoreBucketTable(navigate, slice, config);
    };

    const handlePieArcClick = (datum: ComputedDatum<ScoreDistributionSlice>) => {
      handleScoreBucketClick(datum.data);
    };

    const handlePieLegendClick = (legend: LegendDatum<ScoreDistributionSlice>) => {
      const slice = distributionById.get(String(legend.id));
      if (slice) handleScoreBucketClick(slice);
    };

    return (
      <ChartPanel
        title={`${metric?.label ?? 'Score'} distribution`}
        headerExtra={
          axisMetrics.length > 1 ? (
            <MetricSelect
              label="Distribute by"
              value={metricField}
              options={axisMetrics.map(entry => ({ label: entry.label, value: entry.field }))}
              onChange={value => {
                updatePanelParams(panel.id, { metric: value });
                trackMetricChange('scoreDistribution', 'metric', value);
              }}
            />
          ) : null
        }
      >
        {canFilter ? (
          <p className="gda-analytics-panel__hint">
            Click a segment or legend item to filter the table by score range.
          </p>
        ) : null}
        <div className={`gda-analytics-pie-chart${canFilter ? ' gda-analytics-pie-chart--clickable' : ''}`}>
          <ResponsivePie
            data={distribution}
            margin={{ top: 12, right: 120, bottom: 12, left: 12 }}
            innerRadius={0.58}
            padAngle={1.2}
            cornerRadius={3}
            activeOuterRadiusOffset={8}
            colors={{ datum: 'data.color' }}
            borderWidth={1}
            borderColor={{ from: 'color', modifiers: [['darker', 0.2]] }}
            enableArcLinkLabels={false}
            arcLabelsSkipAngle={12}
            arcLabel={item => (item.value > 0 ? `${item.value}` : '')}
            theme={CHART_THEME}
            onClick={canFilter ? handlePieArcClick : undefined}
            tooltip={({ datum }) => (
              <div className="gda-analytics-tooltip">
                <strong>{datum.label}</strong>
                <div>
                  {datum.value} {config.labels.distributionCountNoun}
                </div>
                {canFilter ? (
                  <div className="gda-analytics-tooltip__action">Click to filter table</div>
                ) : null}
              </div>
            )}
            legends={[
              {
                anchor: 'right',
                direction: 'column',
                translateX: 110,
                itemWidth: 100,
                itemHeight: 18,
                symbolSize: 10,
                symbolShape: 'circle',
                onClick: canFilter ? handlePieLegendClick : undefined,
              },
            ]}
          />
        </div>
      </ChartPanel>
    );
  };

  const renderMetricScatter = (panel: PanelWidgetInstance) => {
    const params = panel.params as MetricScatterParams;
    const xField = params.xMetric || config.scatterTabs[0]?.metricKey || config.fields.ei;
    const yField = params.yMetric || config.fields.score;
    const chartType = params.chartType ?? 'scatter';
    const xMetric = getMetricByField(availableCatalog, xField) ?? getMetricByField(catalog, xField);
    const yMetric = getMetricByField(availableCatalog, yField) ?? getMetricByField(catalog, yField);
    const activeMetricPoints = getMetricScatterData(data, config, xField, yField, 50);
    const scatterLabelIds = pickRandomScatterLabelIds(
      activeMetricPoints.map(point => point.entityId),
      10,
    );
    const scatterData = [
      {
        id: 'entities',
        data: activeMetricPoints.map(point => ({
          x: point.x,
          y: point.y,
          entityId: point.entityId,
          showLabel: scatterLabelIds.has(point.entityId),
        })),
      },
    ];
    const xScale = getDomainScale(xMetric?.domain ?? { kind: 'auto' });
    const yScale = getDomainScale(yMetric?.domain ?? { kind: 'auto' });
    const presetTabs = config.scatterTabs.map(tab => ({
      label: tab.label,
      value: tab.metricKey,
    }));
    const isBar = chartType === 'horizontalBar' || chartType === 'verticalBar';
    const barData = activeMetricPoints
      .slice()
      .sort((a, b) => b.x - a.x)
      .slice(0, 15)
      .map((point, index) => ({
        label: point.entityId,
        value: point.x,
        color: index === 0 ? BRAND_PINK : BRAND_NAVY,
      }));

    return (
      <ChartPanel
        title={
          isBar
            ? `Top ${barData.length} by ${xMetric?.label ?? 'metric'}`
            : resolveScatterTitle(config, xMetric, yMetric)
        }
        className="gda-analytics-panel--wide"
        tabs={!isBar && presetTabs.length ? presetTabs : undefined}
        activeTab={xField}
        onTabChange={
          !isBar && presetTabs.length
            ? value => {
                updatePanelParams(panel.id, { xMetric: value, yMetric: config.fields.score });
                trackMetricChange('metricScatter', 'xMetric', value);
              }
            : undefined
        }
        headerExtra={
          <div className="gda-analytics-panel__metric-pair">
            <MetricSelect
              label="Chart"
              value={chartType}
              options={[
                { label: 'Scatter', value: 'scatter' },
                { label: 'Horizontal bars', value: 'horizontalBar' },
                { label: 'Vertical bars', value: 'verticalBar' },
              ]}
              onChange={value => {
                updatePanelParams(panel.id, {
                  chartType: value as MetricScatterParams['chartType'],
                });
                trackEvent({
                  name: 'dashboard_chart_type_change',
                  buttonId: 'dashboard_scatter_chart_type',
                  page: associationTab,
                  section: 'analytics',
                  feature: 'dashboard_customization',
                  eventCategory: 'dashboard_customization',
                  additionalParams: { widget_type: 'metricScatter', chart_type: value },
                });
              }}
            />
            <MetricSelect
              label={isBar ? 'Rank by' : 'X axis'}
              value={xField}
              options={axisMetrics.map(entry => ({ label: entry.label, value: entry.field }))}
              onChange={value => {
                updatePanelParams(panel.id, { xMetric: value });
                trackMetricChange('metricScatter', 'xMetric', value);
              }}
            />
            {!isBar ? (
              <MetricSelect
                label="Y axis"
                value={yField}
                options={axisMetrics.map(entry => ({ label: entry.label, value: entry.field }))}
                onChange={value => {
                  updatePanelParams(panel.id, { yMetric: value });
                  trackMetricChange('metricScatter', 'yMetric', value);
                }}
              />
            ) : null}
          </div>
        }
      >
        {isBar ? (
          <div className="gda-analytics-bar-chart">
            <ResponsiveBar
              data={barData as BarDatum[]}
              keys={['value']}
              indexBy="label"
              layout={chartType === 'horizontalBar' ? 'horizontal' : 'vertical'}
              margin={
                chartType === 'horizontalBar'
                  ? { top: 12, right: 48, bottom: 36, left: 120 }
                  : { top: 16, right: 24, bottom: 84, left: 56 }
              }
              padding={0.28}
              colors={bar => String((bar.data as { color?: string }).color ?? BRAND_NAVY)}
              borderRadius={4}
              theme={CHART_THEME}
              enableLabel
              labelPosition="end"
              labelOffset={6}
              labelTextColor={BRAND_NAVY}
              axisBottom={
                chartType === 'horizontalBar'
                  ? { legend: xMetric?.label ?? '', legendPosition: 'middle', legendOffset: 28 }
                  : {
                      tickRotation: -45,
                      tickSize: 0,
                      tickPadding: 12,
                      renderTick: renderTruncatedBarChartTick,
                    }
              }
              axisLeft={
                chartType === 'horizontalBar'
                  ? { tickSize: 0, tickPadding: 8 }
                  : {
                      legend: xMetric?.label ?? '',
                      legendPosition: 'middle',
                      legendOffset: -48,
                    }
              }
              tooltip={({ indexValue, value }) => (
                <div className="gda-analytics-tooltip">
                  <strong>{indexValue}</strong>
                  <div>
                    {xMetric?.label}: {formatMetricValue(Number(value), xMetric?.format ?? 'decimal3')}
                  </div>
                </div>
              )}
            />
          </div>
        ) : (
          <div className="gda-analytics-scatter-chart">
            <ResponsiveScatterPlot
              data={scatterData}
              layers={['grid', 'axes', 'nodes', ScatterPlotLabelsLayer, 'legends']}
              margin={{ top: 28, right: 24, bottom: 56, left: 64 }}
              xScale={{
                type: 'linear',
                min: xScale.min,
                max: xScale.max,
              }}
              yScale={{
                type: 'linear',
                min: yScale.min === 'auto' ? (config.tab === 'DDA' ? 'auto' : 0) : yScale.min,
                max: yScale.max === 'auto' ? (config.tab === 'DDA' ? 'auto' : 1) : yScale.max,
              }}
              axisBottom={{
                legend: xMetric?.label ?? '',
                legendPosition: 'middle',
                legendOffset: 42,
              }}
              axisLeft={{
                legend: yMetric?.label ?? config.labels.scatterY,
                legendPosition: 'middle',
                legendOffset: -50,
              }}
              colors={[BRAND_NAVY]}
              nodeSize={10}
              theme={CHART_THEME}
              tooltip={({ node }) => (
                <div className="gda-analytics-tooltip">
                  <strong>{node.data.entityId}</strong>
                  <div>
                    {xMetric?.label}: {Number(node.data.x).toFixed(3)}
                  </div>
                  <div>
                    {yMetric?.label ?? config.labels.scatterY}: {Number(node.data.y).toFixed(3)}
                  </div>
                </div>
              )}
            />
          </div>
        )}
      </ChartPanel>
    );
  };

  const renderEvidenceProfile = () => (
    <ChartPanel title="Evidence profile" className="gda-analytics-panel--wide">
      <AcademicPlanFeatureGate featureName="Evidence profile" isRestricted={isEvidenceProfileRestricted}>
        <EvidenceDistributionGrid
          metrics={evidenceMetrics}
          isLoading={isEvidenceLoading}
          badge={evidenceCoverage.summary ?? undefined}
        />
      </AcademicPlanFeatureGate>
    </ChartPanel>
  );

  const renderColumnChart = (panel: PanelWidgetInstance) => {
    const params = panel.params as ColumnChartParams;
    const column =
      getChartableByField(availableChartableColumns, params.field) ??
      getChartableByField(chartableColumns, params.field);
    if (!column) return null;

    const metric =
      column.kind === 'numeric'
        ? (getMetricByField(availableCatalog, column.field) ?? getMetricByField(catalog, column.field))
        : undefined;

    return (
      <ColumnChartWidget
        column={column}
        data={data}
        domain={metric?.domain}
        chartType={params.chartType}
        countNoun={config.labels.distributionCountNoun}
        onChartTypeChange={chartType => {
          updatePanelParams(panel.id, { chartType });
          trackEvent({
            name: 'dashboard_chart_type_change',
            buttonId: 'dashboard_chart_type_change',
            page: associationTab,
            section: 'analytics',
            feature: 'dashboard_customization',
            eventCategory: 'dashboard_customization',
            additionalParams: {
              field: column.field,
              chart_type: chartType,
              kind: column.kind,
            },
          });
        }}
      />
    );
  };

  const renderPanel = (panel: PanelWidgetInstance): ReactNode => {
    switch (panel.type) {
      case 'topEntities':
        return renderTopEntities(panel);
      case 'publicationTrend':
        return renderPublicationTrend();
      case 'scoreDistribution':
        return renderScoreDistribution(panel);
      case 'metricScatter':
        return renderMetricScatter(panel);
      case 'evidenceProfile':
        return renderEvidenceProfile();
      case 'columnChart':
        return renderColumnChart(panel);
      default:
        return null;
    }
  };

  const allPanelsHidden = visiblePanels.length === 0;

  return (
    <div ref={dashboardRef} className="gda-analytics-dashboard mr-2">
      <header className="gda-analytics-header">
        <div className="gda-analytics-header__title-row">
          <h2 className="gda-analytics-header__title">{config.analyticsTitle}</h2>
          <SharedBadge
            text={`Source: ${sourceLabel}`}
            variant="primaryLight"
            size="xs"
            className="gda-analytics-header__source-badge"
          />
        </div>
        <div className="gda-analytics-header__actions">
          <DashboardCustomizer
            config={config}
            catalog={availableCatalog.length ? availableCatalog : catalog}
            chartableColumns={availableChartableColumns.length ? availableChartableColumns : chartableColumns}
            data={data}
            layout={layout}
            isCustomized={isCustomized}
            columnChartCount={columnChartCount}
            maxColumnCharts={maxColumnCharts}
            onTogglePanel={togglePanel}
            onToggleCard={toggleCard}
            onMovePanel={movePanel}
            onReorderPanel={reorderPanel}
            onUpdatePanelParams={updatePanelParams}
            onUpdateCardMetric={updateCardMetric}
            onAddColumnChart={addColumnChart}
            onAddColumnCharts={addColumnCharts}
            onRemovePanel={removePanel}
            onReset={resetLayout}
          />
          <SharedButton
            variant="secondaryOutline"
            size="xs"
            className={`gda-analytics-header__download${showEntityPicker ? ' mr-0' : ''}`}
            disabled={isDownloadingPng}
            onClick={handleDownloadPng}
            aria-label={isDownloadingPng ? 'Downloading PNG' : 'Download PNG'}
            title={isDownloadingPng ? 'Downloading...' : 'Download PNG'}
            iconLeft={<ArrowDownTrayIcon className="h-4 w-4" />}
            iconMargin="gda-analytics-header__action-icon"
          >
            <span className="gda-analytics-header__action-label">
              {isDownloadingPng ? 'Downloading...' : 'Download PNG'}
            </span>
          </SharedButton>
          {showEntityPicker && (
            <SharedButton
              variant="secondaryOutline"
              size="xs"
              className="gda-analytics-header__download"
              disabled={!selectedEntityId}
              onClick={() => navigateToSelectedEntityTable(navigate, view, selectedEntityId)}
              aria-label="View filtered table"
              title="View filtered table"
              iconLeft={<TableCellsIcon className="h-4 w-4" />}
              iconMargin="gda-analytics-header__action-icon"
            >
              <span className="gda-analytics-header__action-label">View filtered table</span>
            </SharedButton>
          )}
        </div>
      </header>

      {visibleCards.length > 0 ? (
        <div className="gda-analytics-summary">
          {visibleCards.map(cardInstance => {
            const card = config.summaryCards.find(entry => entry.id === cardInstance.id);
            if (!card) return null;

            let value: number;
            let label: string;
            let decimals = card.decimals ?? 0;

            if (cardInstance.metric && !isPresetSummaryCard(cardInstance.id)) {
              const metric =
                getMetricByField(availableCatalog, cardInstance.metric) ??
                getMetricByField(catalog, cardInstance.metric);
              value = averageField(data, cardInstance.metric);
              label = metric ? `Average ${metric.label}` : getSummaryCardLabel(card, currentYear);
              decimals = metric?.format === 'integer' ? 0 : metric?.format === 'decimal3' ? 3 : 2;
            } else {
              value = getSummaryCardValue(card, data, config, summaryMatchCardContext);
              label = getSummaryCardLabel(card, currentYear);
            }

            return <SummaryCard key={card.id} label={label} value={formatMetricNumber(value, decimals)} />;
          })}
        </div>
      ) : null}

      <div className="gda-analytics-entity-layout">
        {showEntityPicker ? (
          <aside
            className="gda-analytics-entity-picker"
            style={
              entityPickerMaxHeight != null
                ? {
                    maxHeight: entityPickerMaxHeight,
                    height: entityPickerMaxHeight,
                  }
                : undefined
            }
          >
            <div className="gda-analytics-entity-picker__title">{entityPickerTitle}</div>
            <div className="gda-analytics-entity-picker__list">
              {names.map(item => {
                const label = item.label ?? item.name ?? item.id;
                const isActive = item.id === selectedEntityId;
                return (
                  <button
                    key={item.id}
                    type="button"
                    className={`gda-analytics-entity-picker__item ${
                      isActive ? 'gda-analytics-entity-picker__item--active' : ''
                    }`}
                    onClick={() => setSelectedEntityId(item.id)}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </aside>
        ) : null}

        <div className="gda-analytics-entity-layout__content" ref={graphsContentRef}>
          {allPanelsHidden ? (
            <div className="gda-analytics-empty-layout" role="status">
              <p className="gda-analytics-empty-layout__title">All panels are hidden</p>
              <p className="gda-analytics-empty-layout__text">
                Choose panels in Customize, or restore the default layout.
              </p>
              <SharedButton variant="secondaryOutline" size="xs" onClick={resetLayout}>
                Reset layout
              </SharedButton>
            </div>
          ) : (
            <div className="gda-analytics-widgets">
              {(() => {
                const spans = resolveWidgetSpans(visiblePanels);
                return visiblePanels.map(panel => {
                  const span = spans.get(panel.id) ?? getPanelDefinition(panel.type)?.span ?? 'half';
                  return (
                    <div
                      key={panel.id}
                      className={`gda-analytics-widgets__item gda-analytics-widgets__item--${span}`}
                    >
                      {renderPanel(panel)}
                    </div>
                  );
                });
              })()}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AssociationAnalyticsDashboard;
